import { FC } from 'react';
import { TbTimeline } from 'react-icons/tb';

import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import MetricComparisonDropdown from '@/shared/components/dropdowns/MetricComparisonDropdown';
import MetricLineItemDropdown from '@/shared/components/dropdowns/MetricLineItemDropdown';
import MetricLineItems from '@/shared/components/layouts/rows/MetricLineItems';
import type { SetStateMethod } from '@/types';

import { FinancialOverviewChartHeaderProps, FinancialOverviewChartMetric } from '../../types';
import { FinancialOverviewChartRangeSegmented } from '../FinancialOverviewChartRangeSegmented';
import { FinancialOverviewKeyEventsDropdown } from '../FinancialOverviewKeyEventsDropdown';

const metricOptions: FinancialOverviewChartMetric[] = ['price', 'volume', 'changePercent'];
const metricLabels: Record<FinancialOverviewChartMetric, string> = {
    changePercent: 'Change %',
    price: 'Price',
    volume: 'Volume',
};

const isChartMetric = (value: string): value is FinancialOverviewChartMetric =>
    metricOptions.includes(value as FinancialOverviewChartMetric);

export const FinancialOverviewChartHeader: FC<FinancialOverviewChartHeaderProps> = ({
    chartRange,
    comparisonCompanies,
    comparisonCompany,
    keyEvents,
    metric,
    onComparisonCompaniesChange,
    onKeyEventsChange,
    onMetricChange,
    setChartRange,
}) => {
    const marketIsOpen = true;
    const metricTags = [metric];
    const setChartedMetric: SetStateMethod<string> = () => undefined;
    const setMetricTags: SetStateMethod<string[]> = (value) => {
        const nextTags = typeof value === 'function' ? value(metricTags) : value;
        const nextMetric = [...nextTags].reverse().find(isChartMetric);

        if (nextMetric) {
            onMetricChange(nextMetric);
        }
    };

    return (
        <div style={{ maxWidth: '100%', minWidth: 0 }}>
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    justifyContent: 'space-between',
                    maxWidth: '100%',
                    minWidth: 0,
                }}
            >
                <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minWidth: 0 }}>
                    <MetricLineItemDropdown
                        lineItemLabels={metricLabels}
                        lineItems={metricOptions}
                        setChartedLineItem={setChartedMetric}
                        setTags={setMetricTags}
                        tags={metricTags}
                    >
                        <DropdownButton icon={<TbTimeline />} size='small'>
                            {metricLabels[metric]}
                        </DropdownButton>
                    </MetricLineItemDropdown>
                    <FinancialOverviewKeyEventsDropdown onChange={onKeyEventsChange} value={keyEvents} />
                    {comparisonCompany ? (
                        <MetricComparisonDropdown
                            company={comparisonCompany}
                            onChange={onComparisonCompaniesChange}
                            value={comparisonCompanies}
                        />
                    ) : null}
                </div>
                <div
                    style={{
                        flex: '0 1 auto',
                        marginLeft: 'auto',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflowX: 'auto',
                    }}
                >
                    <FinancialOverviewChartRangeSegmented
                        chartRange={chartRange}
                        marketIsOpen={marketIsOpen}
                        setChartRange={setChartRange}
                    />
                </div>
            </div>
            <MetricLineItems
                companies={comparisonCompany ? [comparisonCompany, ...comparisonCompanies] : comparisonCompanies}
                lineItemLabels={metricLabels}
                onComparisonCompaniesChange={onComparisonCompaniesChange}
                setTags={setMetricTags}
                tags={metricTags}
                view='overview'
            />
        </div>
    );
};
